import React, { useState, useEffect } from "react";
import { Chess } from "chess.js";
import {
  sendMessage,
  addListener,
  removeListener,
} from "../services/websocketManager";
import { LottieAnimation } from "../lottie/LottieAnimation.js";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogActions, Button } from "@mui/material";
import { Chessboard } from "react-chessboard";
import Typography from "@mui/material/Typography";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { useUser } from "../authMethods/UserContext";
import { GENERAL } from "../general/General";
import SearchIcon from "@mui/icons-material/Search";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { resolveCanonicalValue } from "../services/resolveCanonicalValue";
import { Switch, FormControlLabel, Box } from "@mui/material";

import MoveLog from "./MoveLog";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import PaymentConfirmationDialog from "./PaymentConfirmationDialog.js";
import { generalMessageTemplate } from "../entities/generalMessageTemplate";

const UserGames = ({ updateBackground }) => {
  const { t } = useTranslation();
  const [games, setGames] = useState([]);
  const navigate = useNavigate();
  const { userData } = useUser(); // 🔄 Pull globally from context
  const [searchTriggered, setSearchTriggered] = useState(false);
  // State
  const [game] = useState(new Chess());
  let [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc"); // 'asc' or 'desc'
  const [loading, setLoading] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [totalItems, setTotalItems] = useState(0);

  // State for Dialog (More Info)
  const [selectedGame, setSelectedGame] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [fenPosition, setFenPosition] = useState(""); // FEN string for chess board
  const desktopScreen = "509";
  const mobileScreen = "250";

  const [useAndSearch, setUseAndSearch] = useState(false); // false = OR, true = AND

  // Coins action ////////
  const [isCoinsDialogOpen, setIsCoinsDialogOpen] = useState(false);
 

  const handleConfirm = async () => {
    
    setIsCoinsDialogOpen(false);
     const generalMessage = { ...generalMessageTemplate };
        generalMessage.messageType = "analitics_request";
        generalMessage.language = localStorage.getItem("i18nextLng") || "en"; 
        generalMessage.gameInfo.gameId = selectedGame.gameId;
        generalMessage.gameInfo.basicGameHistory =
          localStorage.getItem("moveHistory");
        generalMessage.gameInfo.color = selectedGame.color;
        generalMessage.gameInfo.pgn = selectedGame.pgn;
        generalMessage.gameInfo.gameTeamType = selectedGame.gameTeamType;
        sendMessage(generalMessage);
        navigate("/loader", {
          state: {
            boardWidth: 400,
            message: "Wait_for_analyzing", 
            lottieUrl:
              "https://res.cloudinary.com/dnlgtek2a/raw/upload/v1743418492/Lottie/analysis.json",
          },
        });
  };

  const handlePurchaseCoins = () => {
    setIsCoinsDialogOpen(false);
    alert('Redirecting to coin purchase...');
  };

  ////////////////////////

 

  useEffect(() => {
    updateBackground({
      image: GENERAL.BACKGROUND,
    });

    // Define a function to handle WebSocket events
    const handleWebSocketEvent = (event) => {
      if (
        event.type === "message" &&
        event.message.messageType === "game_history_response"
      ) {
        const gameData = event.message?.gameSearchResponse?.games || [];

        setGames(gameData);
        setTotalItems(event.message?.gameSearchResponse?.totalCount || 0);
        setLoading(false); // Stop loading spinner
      }
    };

    // Subscribe to WebSocket events
    addListener(handleWebSocketEvent);

    // Initial default load of last 5 games in descending order
    if (!searchTriggered && userData?.id) {
      setLoading(true);
      requestGames(1, 5, "", "startGameTime", "desc", true);
      setSearchTriggered(true);
    }

    // Cleanup on component unmount
    return () => {
      removeListener(handleWebSocketEvent);
    };
  }, [
    currentPage,
    itemsPerPage,
    searchQuery,
    sortBy,
    sortDirection,
    updateBackground,
  ]);

  function requestGames(page, perPage, search, sortBy, sortDir, runQuery) {
    if (runQuery === false) return;
    const message = {
      messageType: "game_history_request",
      gameSearchRequest: {
        page: page - 1,
        pageSize: perPage,
        searchQuery: search,
        sortBy,
        sortDirection: sortDir,
        userId: userData?.id,
        searchOperator: useAndSearch ? "AND" : "OR",
      },
    };
    sendMessage(message);
  }

  const handleSearch = async (e) => {
    e.preventDefault();

    setLoading(true);

    if (searchQuery !== "") {
      searchQuery = await resolveCanonicalValue(searchQuery);
    }

    setCurrentPage(1);
    setSearchTriggered(true);

    requestGames(1, itemsPerPage, searchQuery, sortBy, sortDirection, true);
  };

  const requestSort = (key) => {
    let direction = sortDirection === "asc" ? "desc" : "asc";
    setSortBy(key);
    setSortDirection(direction);
    setCurrentPage(1);
    setSearchTriggered(true);
    setLoading(true);
    requestGames(1, itemsPerPage, searchQuery, key, direction, true);
  };

  const jumpToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
    setSearchTriggered(true);
    setLoading(true);
    requestGames(
      pageNumber,
      itemsPerPage,
      searchQuery,
      sortBy,
      sortDirection,
      true
    );
    window.scrollTo(0, 0);
  };

  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = Number(e.target.value);
    const firstItemIndex = (currentPage - 1) * itemsPerPage;
    const newCurrentPage = Math.floor(firstItemIndex / newItemsPerPage) + 1;
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(newCurrentPage);
    setSearchTriggered(true);
    setLoading(true);
    requestGames(
      newCurrentPage,
      newItemsPerPage,
      searchQuery,
      sortBy,
      sortDirection,
      true
    );
  };

  useEffect(() => {
    if (userData) {
      if (!userData.email && !userData.socialUser) {
        Swal.fire({
          title: t("Please log in to view your game history"),
          icon: "warning",
          draggable: true,
          confirmButtonText: t("OK"),
        });
        navigate("/login");
      }
    } else {
      Swal.fire({
        title: t("Please log in to view your game history"),
        icon: "warning",
        draggable: true,
        confirmButtonText: t("OK"),
      });
      navigate("/login");
    }
  }, [navigate, t, userData]);

  const styles = {
    container: {
      width: "100%",
      padding: "20px",
      backgroundColor: "white",
      borderRadius: "8px",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)",
      fontFamily: "Arial, sans-serif",
    },
    title: {
      fontSize: "1.5rem",
      fontWeight: "bold",
      marginBottom: "1rem",
    },
    searchContainer: {
      position: "relative",
      marginBottom: "1rem",
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    searchIcon: {
      position: "absolute",
      left: "10px",
      top: "50%",
      transform: "translateY(-50%)",
      color: "#9ca3af",
    },
    searchInput: {
      width: "100%",
      padding: "8px 8px 8px 36px",
      fontSize: "0.875rem",
      border: "1px solid #d1d5db",
      borderRadius: "4px",
    },
    searchButton: {
      padding: "8px 16px",
      backgroundColor: "#4f46e5",
      color: "white",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "0.875rem",
      transition: "background-color 0.2s",
    },
    searchButtonHover: {
      backgroundColor: "#4338ca",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      marginBottom: "1rem",
    },
    tableHead: {
      backgroundColor: "#f9fafb",
    },
    tableHeadCell: {
      padding: "12px 16px",
      textAlign: "left",
      fontSize: "0.75rem",
      fontWeight: "600",
      textTransform: "uppercase",
      color: "#6b7280",
      cursor: "pointer",
    },
    tableCell: {
      padding: "16px",
      fontSize: "0.875rem",
      borderBottom: "1px solid #e5e7eb",
    },
    tableRow: {
      transition: "background-color 0.2s",
    },
    tableRowHover: {
      backgroundColor: "#f9fafb",
    },
    actionButton: {
      backgroundColor: "#e0e7ff",
      color: "#4f46e5",
      border: "none",
      padding: "6px 12px",
      borderRadius: "9999px",
      fontSize: "0.875rem",
      cursor: "pointer",
      transition: "all 0.2s",
    },
    actionButtonHover: {
      backgroundColor: "#c7d2fe",
      color: "#4338ca",
    },
    mobileCard: {
      padding: "16px",
      backgroundColor: "#f9fafb",
      borderRadius: "8px",
      marginBottom: "16px",
      boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    },
    mobileCardHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: "8px",
    },
    mobileCardContent: {
      marginBottom: "4px",
    },
    pagination: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "12px 0",
      borderTop: "1px solid #e5e7eb",
    },
    paginationText: {
      fontSize: "0.875rem",
      color: "#4b5563",
    },
    paginationButton: {
      padding: "8px 12px",
      border: "1px solid #d1d5db",
      backgroundColor: "white",
      borderRadius: "4px",
      marginRight: "4px",
      cursor: "pointer",
    },
    paginationButtonActive: {
      backgroundColor: "#4f46e5",
      color: "white",
      borderColor: "#4f46e5",
    },
    paginationButtonDisabled: {
      backgroundColor: "#f3f4f6",
      color: "#9ca3af",
      cursor: "not-allowed",
    },
    paginationControls: {
      display: "flex",
      alignItems: "center",
      gap: "4px",
    },
    itemsPerPageContainer: {
      display: "flex",
      alignItems: "center",
      marginTop: "16px",
      justifyContent: "space-between",
    },
    itemsPerPageLabel: {
      fontSize: "0.875rem",
      color: "#4b5563",
      marginRight: "8px",
    },
    itemsPerPageSelect: {
      padding: "4px 8px",
      borderRadius: "4px",
      border: "1px solid #d1d5db",
      fontSize: "0.875rem",
    },
    hidden: {
      display: "none",
    },
    visible: {
      display: "block",
    },
    pageInfo: {
      fontSize: "0.875rem",
      color: "#4b5563",
      marginLeft: "8px",
      display: "inline-block",
    },
    loaderContainer: {
      display: "flex",
      justifyContent: "center",
      padding: "20px 0",
    },
    paginationModeSwitch: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    switchLabel: {
      fontSize: "0.875rem",
      color: "#4b5563",
    },
    switchInput: {
      position: "relative",
      display: "inline-block",
      width: "40px",
      height: "20px",
    },
    switchSlider: {
      position: "absolute",
      cursor: "pointer",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "#ccc",
      transition: "0.4s",
      borderRadius: "34px",
    },
    switchSliderBefore: {
      position: "absolute",
      content: '""',
      height: "16px",
      width: "16px",
      left: "2px",
      bottom: "2px",
      backgroundColor: "white",
      transition: "0.4s",
      borderRadius: "50%",
    },
    switchSliderChecked: {
      backgroundColor: "#4f46e5",
    },
    switchSliderBeforeChecked: {
      transform: "translateX(20px)",
    },
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [hoverRowId, setHoverRowId] = useState(null);
  const [hoverButtonId, setHoverButtonId] = useState(null);
  const [searchButtonHover, setSearchButtonHover] = useState(false);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle info button click
  const handleInfoClick = (game) => {
    openDialog(game);
  };

  // Calculate total pages
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Get sort direction indicator
  const getSortIndicator = (key) => {
    if (sortBy === key) {
      return sortDirection === "asc" ? " ▲" : " ▼";
    }
    return "";
  };

  // Generate page numbers to display (with ellipsis for large page counts)
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5; // Number of page buttons to show

    if (totalPages <= maxVisiblePages) {
      // If not many pages, show all page numbers
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Complex pagination with ellipsis
      if (currentPage <= 3) {
        // Current page is near the start
        for (let i = 1; i <= 4; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push("...");
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Current page is near the end
        pageNumbers.push(1);
        pageNumbers.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        // Current page is in the middle
        pageNumbers.push(1);
        pageNumbers.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push("...");
        pageNumbers.push(totalPages);
      }
    }

    return pageNumbers;
  };

  const handleAnalyticsClick = (gameInfo) => {
    if(gameInfo.analytics || gameInfo.chatAnalytics){
      handleConfirm();
      return;
    }
    //console.log(userData?.userStatistics?.coins);
    setIsCoinsDialogOpen(true);
   
  };
  // Handle direct page input
  const handlePageInputChange = (e) => {
    const value = parseInt(e.target.value);
    if (!isNaN(value) && value >= 1 && value <= totalPages) {
      setCurrentPage(value);
    }
  };

  const openDialog = (game) => {
    setSelectedGame(game);
    setFenPosition(game.fen || ""); // Set FEN position for chess board
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setSelectedGame(null);
  };

  // Calculate current range for display
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(startIndex + itemsPerPage - 1, totalItems);

  // ✅ Mobile-only controls below search bar
  const mobileSearchControls = isMobile && (
    <div
      style={{
        marginTop: "10px",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
      }}
    >
      <FormControlLabel
        control={
          <Switch
            checked={useAndSearch}
            onChange={(e) => setUseAndSearch(e.target.checked)}
            color="primary"
          />
        }
        label={useAndSearch ? t("Search Mode: AND") : t("Search Mode: OR")}
      />
      <div style={{ display: "flex", gap: "10px" }}>
        <button
          type="submit"
          style={{
            ...styles.searchButton,
            ...(searchButtonHover ? styles.searchButtonHover : {}),
          }}
          onMouseEnter={() => setSearchButtonHover(true)}
          onMouseLeave={() => setSearchButtonHover(false)}
        >
          {t("Search")}
        </button>
        {searchQuery && (
          <button
            type="button"
            onClick={() => {
              setSearchQuery("");
              setSearchTriggered(true);
              setCurrentPage(1);
              setLoading(true);
              requestGames(1, itemsPerPage, "", sortBy, sortDirection, true);
            }}
            style={{
              ...styles.searchButton,
              backgroundColor: "red",
              color: "white",
            }}
          >
            {t("Clear")}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div style={styles.container}>
      <PaymentConfirmationDialog
        isOpen={isCoinsDialogOpen}
        onClose={() => setIsCoinsDialogOpen(false)}
        onConfirm={handleConfirm}
        onPurchaseCoins={handlePurchaseCoins}
        title={t("Confirm Feature Purchase")}
        description={t("Unlock advanced analytics and detailed move analysis for your chess games.")}
        coinsRequired={50}
        currentCoins={userData?.userStatistics?.coins || 0}
        feature={t("Premium Chess Analytics")}
      />
      {/* Dialog for Game Info */}
      <Dialog
        sx={{
          zIndex: 3000,
          "& .MuiDialog-paper": {
            borderRadius: "15px",
            padding: "20px",
            boxShadow: "0 4px 20px rgba(141, 73, 73, 0.3)",

            maxWidth: "100%",
          },
        }}
        open={isDialogOpen}
        onClose={closeDialog}
      >
        {selectedGame && (
          <>
            {/* <DialogTitle
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "1.4rem",
              }}
            >
              {t("Game Details")}
            </DialogTitle> */}
            <DialogContent>
              <Accordion>
                <AccordionSummary
                  style={{
                    justifyContent: "center",
                    alignItems: "center",

                    border:
                      isMobile === false ? "5px solid #ccc" : "5px solid #ccc", // Add border around the Stack
                    borderRadius: isMobile === false ? "15px" : "15px", // Rounded corners
                    boxShadow:
                      isMobile === false
                        ? "0 5px 5px black"
                        : "0 1px 1px black",
                    maxWidth: "100%",

                    position: "relative",
                    zIndex: 10,
                  }}
                  expandIcon={<ExpandMoreIcon />}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                    {t("Game Info")}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="game-info">
                    <p>
                      <strong>{t("Game ID/Name")}:</strong>{" "}
                      {selectedGame?.gameId}
                      {selectedGame?.gameName
                        ? ` - ( ${selectedGame.gameName})`
                        : ""}
                    </p>
                    <p>
                      <strong>{t("Date")}:</strong> {selectedGame.startGameTime}
                    </p>
                    <p>
                      <strong>{t("White Player")}:</strong>{" "}
                      {selectedGame.color === "white"
                        ? selectedGame.name
                        : (selectedGame.opponentUsers?.[0].name ??
                          t("Unknown"))}
                    </p>
                    <p>
                      <strong>{t("Black Player")}:</strong>{" "}
                      {selectedGame.color === "black"
                        ? selectedGame.name
                        : (selectedGame.opponentUsers?.[0].name ??
                          t("Unknown"))}
                    </p>
                    <p>
                      <strong>{t("Status")}:</strong>{" "}
                      {t(selectedGame.gameStatus)}
                    </p>
                    <p>
                      <strong>{t("Opponent Type")}:</strong>{" "}
                      {t(selectedGame.opponentType)}
                    </p>
                    <p>
                      <strong>{t("Game Type")}:</strong>{" "}
                      {t(selectedGame.gameTeamType)}
                    </p>
                  </div>
                         <Button
                variant="contained"
                color="primary"
                onClick={() => handleAnalyticsClick(selectedGame)}
                startIcon={<AutoGraphIcon sx={{ fontSize: 20 }} />}
              >
                {t("Analyze Game")}
              </Button>
                </AccordionDetails>
              </Accordion>

              {/* Chess Board (FEN Position) */}
              <div style={{ textAlign: "center", marginTop: "20px" }}>
                {/* <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", marginBottom: "12px" }}
                >
                  {t("Game Board")}
                </Typography> */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: window.innerWidth <= 600 ? "column" : "row",
                    justifyContent: "center",
                    alignItems:
                      window.innerWidth <= 600 ? "center" : "flex-start",
                    gap: window.innerWidth <= 600 ? "12px" : "20px",
                    marginTop: "30px",
                  }}
                >
                  <div
                    style={{
                      justifyContent: "center",
                      alignItems: "center",

                      border:
                        isMobile === false
                          ? "15px solid #ccc"
                          : "5px solid #ccc", // Add border around the Stack
                      borderRadius: isMobile === false ? "15px" : "15px", // Rounded corners
                      boxShadow:
                        isMobile === false
                          ? "0 25px 25px black"
                          : "0 25px 25px black",
                      backgroundColor: isMobile === false ? "black" : "black", // Optional background color

                      position: "relative",
                      zIndex: 10,
                    }}
                  >
                    <Chessboard
                      position={fenPosition || "start"}
                      boardOrientation={selectedGame.color}
                      boardWidth={
                        window.innerWidth > 600 ? desktopScreen : mobileScreen
                      }
                    />
                  </div>
                  <div
                    style={{
                      marginTop: window.innerWidth <= 600 ? "12px" : "0px",
                      display: "flex",
                      justifyContent:
                        window.innerWidth <= 600 ? "center" : "flex-start",
                    }}
                  >
                    <Box
                      sx={{
                        px: 2,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <MoveLog
                        isMobileView={window.innerWidth <= 600}
                        t={t}
                        getMoveHistory={() => selectedGame.simpleGameHistory}
                        getCurrentMoveIndex={() => 0}
                        setCurrentMoveIndex={() => {}}
                        game={game}
                        setFen={setFenPosition}
                        updateCapturedPieces={() => {}}
                        disableClick={false}
                        onClose={() => setIsDialogOpen(false)}
                        selectedGame={selectedGame}
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleAnalyticsClick(selectedGame)}
                        startIcon={<AutoGraphIcon sx={{ fontSize: 20 }} />}
                      >
                        {t("Analyze Game")}
                      </Button>
                    </Box>
                  </div>
                </div>
              </div>
            </DialogContent>

            <DialogActions
              sx={{
                justifyContent: "center", // Center-align the buttons
                paddingBottom: "16px",
              }}
            >
              {/* <Button
                variant="contained"
                color="primary"
                onClick={() => alert("Game Log Coming Soon!")}
              >
                {t("Show Game Log")}
              </Button> */}
              <Button
                variant="outlined"
                color="secondary"
                onClick={closeDialog}
              >
                {t("Close")}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <h2 style={styles.title}>{t("Chess Games")}</h2>

      {/* Search Form */}
      <form onSubmit={handleSearch}>
        <div style={styles.searchContainer}>
          <div style={{ position: "relative", flexGrow: 1 }}>
            <div style={styles.searchIcon}>
              <SearchIcon style={{ color: "#aaa", fontSize: 20 }} />
            </div>
            <input
              type="text"
              style={styles.searchInput}
              placeholder={t("Search games...") + t("Search games example")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {!isMobile && (
            <>
              <FormControlLabel
                control={
                  <Switch
                    checked={useAndSearch}
                    onChange={(e) => setUseAndSearch(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  useAndSearch ? t("Search Mode: AND") : t("Search Mode: OR")
                }
              />
              <button
                type="submit"
                style={{
                  ...styles.searchButton,
                  ...(searchButtonHover ? styles.searchButtonHover : {}),
                }}
                onMouseEnter={() => setSearchButtonHover(true)}
                onMouseLeave={() => setSearchButtonHover(false)}
              >
                {t("Search")}
              </button>
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    setSearchTriggered(true);
                    setCurrentPage(1);
                    setLoading(true);
                    requestGames(
                      1,
                      itemsPerPage,
                      "",
                      sortBy,
                      sortDirection,
                      true
                    );
                  }}
                  style={{
                    ...styles.searchButton,
                    backgroundColor: "red",
                    color: "white",
                  }}
                >
                  {t("Clear")}
                </button>
              )}
            </>
          )}
        </div>
        {/* 👇 only visible on mobile */}
        {mobileSearchControls}
      </form>

      {/* Loading indicator */}
      {loading ? (
        <div style={styles.loaderContainer}>
          <LottieAnimation
            path={
              "https://res.cloudinary.com/dnlgtek2a/raw/upload/v1743418492/Lottie/search.json"
            }
            width={isMobile ? "250px" : "400px"}
            height={isMobile ? "250px" : "400px"}
            autoplay
            loop
          />
        </div>
      ) : games.length === 0 ? (
        <div style={{ textAlign: "center", padding: "20px" }}>
          {t("No games found.")}
        </div>
      ) : (
        <>
          {/* Table for desktop */}
          <div style={isMobile ? styles.hidden : styles.visible}>
            <table style={styles.table}>
              <thead style={styles.tableHead}>
                <tr style={{ backgroundColor: "#4a707d" }}>
                  <th
                    style={{
                      ...styles.tableHeadCell,
                      color: "white",
                      fontWeight: "bold",
                    }}
                    onClick={() => requestSort("gameId")}
                  >
                    {t("Game ID/Name")} {getSortIndicator("gameId")}
                  </th>
                  <th
                    style={{
                      ...styles.tableHeadCell,
                      color: "white",
                      fontWeight: "bold",
                    }}
                    onClick={() => requestSort("startDate")}
                  >
                    {t("Start Date")} {getSortIndicator("startDate")}
                  </th>
                  <th
                    style={{
                      ...styles.tableHeadCell,
                      color: "white",
                      fontWeight: "bold",
                    }}
                    onClick={() => requestSort("whitePlayer")}
                  >
                    {t("White Player")} {getSortIndicator("whitePlayer")}
                  </th>
                  <th
                    style={{
                      ...styles.tableHeadCell,
                      color: "white",
                      fontWeight: "bold",
                    }}
                    onClick={() => requestSort("blackPlayer")}
                  >
                    {t("Black Player")} {getSortIndicator("blackPlayer")}
                  </th>
                  <th
                    style={{
                      ...styles.tableHeadCell,
                      color: "white",
                      fontWeight: "bold",
                    }}
                    onClick={() => requestSort("status")}
                  >
                    {t("Status")} {getSortIndicator("status")}
                  </th>
                  <th
                    style={{
                      ...styles.tableHeadCell,
                      color: "white",
                      fontWeight: "bold",
                    }}
                    onClick={() => requestSort("info")}
                  >
                    {t("Info_1")} {getSortIndicator("info")}
                  </th>
                  <th
                    style={{
                      ...styles.tableHeadCell,
                      color: "white",
                      fontWeight: "bold",
                    }}
                    onClick={() => requestSort("opponent")}
                  >
                    {t("Opponent Type")} {getSortIndicator("opponent")}
                  </th>
                  <th
                    style={{
                      ...styles.tableHeadCell,
                      color: "white",
                      fontWeight: "bold",
                    }}
                    onClick={() => requestSort("gameType")}
                  >
                    {t("Game Type")} {getSortIndicator("gameType")}
                  </th>
                  <th
                    style={{
                      ...styles.tableHeadCell,
                      color: "white",
                      fontWeight: "bold",
                    }}
                  >
                    {t("Actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {games.map((game) => (
                  <tr
                    key={game.id}
                    style={{
                      ...styles.tableRow,
                      ...(hoverRowId === game.id ? styles.tableRowHover : {}),
                    }}
                    onMouseEnter={() => setHoverRowId(game.id)}
                    onMouseLeave={() => setHoverRowId(null)}
                  >
                    <td style={styles.tableCell}>
                      {" "}
                      {game?.gameId}
                      {game?.gameName ? ` - ( ${game?.gameName})` : ""}
                    </td>
                    <td style={styles.tableCell}>{t(game.startGameTime)}</td>
                    <td style={styles.tableCell}>
                      {game.color === "white"
                        ? game.name
                        : (game.opponentUsers?.[0].name ??
                          t("Unknown Opponent"))}
                    </td>
                    <td style={styles.tableCell}>
                      {game.color !== "white"
                        ? game.name
                        : (game.opponentUsers?.[0].name ??
                          t("Unknown Opponent"))}
                    </td>
                    <td style={styles.tableCell}>
                      {t(game.gameGeneralStatus + "_1")}
                    </td>

                    <td style={styles.tableCell}>{t(game.gameStatus)}</td>
                    <td style={styles.tableCell}>{t(game.opponentType)}</td>
                    <td style={styles.tableCell}>{t(game.gameTeamType)}</td>
                    <td style={styles.tableCell}>
                      <button
                        onClick={() => handleInfoClick(game)}
                        style={{
                          ...styles.actionButton,
                          ...(hoverButtonId === game.id
                            ? styles.actionButtonHover
                            : {}),
                        }}
                        onMouseEnter={() => setHoverButtonId(game.id)}
                        onMouseLeave={() => setHoverButtonId(null)}
                      >
                        {t("More Info")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Cards for mobile */}
          <div style={isMobile ? styles.visible : styles.hidden}>
            {games.map((game) => (
              <div key={game.id} style={styles.mobileCard}>
                <div style={styles.mobileCardHeader}>
                  <div>
                    <div style={{ fontWeight: "regular" }}>
                      {t(game.startGameTime)}
                    </div>
                    <div style={{ fontWeight: "regular" }}>
                      {game.color === "white"
                        ? game.name
                        : (game.opponentUsers?.[0].name ??
                          t("Unknown Opponent"))}
                    </div>
                    <div style={{ color: "#6b7280" }}>
                      vs.{" "}
                      {game.color !== "white"
                        ? game.name
                        : (game.opponentUsers?.[0].name ??
                          t("Unknown Opponent"))}
                    </div>
                    <div style={{ marginTop: "8px" }}>
                      {t("Result")}:{" "}
                      <span style={{ fontWeight: "bold" }}>
                        {t(game.gameGeneralStatus + "_1")}
                      </span>
                    </div>
                    <div style={{ fontWeight: "regular" }}>
                      {t(game.gameStatus)}
                    </div>
                  </div>
                  <button
                    onClick={() => handleInfoClick(game)}
                    style={styles.actionButton}
                  >
                    {t("More Info")}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Improved Pagination Controls */}
          <div style={styles.pagination}>
            <div>
              <p style={styles.paginationText}>
                {t("Showing")} {totalItems > 0 ? startIndex : 0} {t("to")}{" "}
                {endIndex} {t("of")} {totalItems} {t("results")}
              </p>
            </div>

            <div style={styles.paginationControls}>
              {/* Jump to first page */}
              <button
                onClick={() => jumpToPage(1)}
                //disabled={currentPage === 1 || totalPages === 0}
                style={{
                  ...styles.paginationButton,
                  ...(currentPage === 1 || totalPages === 0
                    ? styles.paginationButtonDisabled
                    : {}),
                }}
                aria-label="First page"
              >
                &laquo;&laquo;
              </button>

              {/* Previous page */}
              <button
                onClick={() => jumpToPage(currentPage - 1)}
                //disabled={currentPage === 1 || totalPages === 0}
                style={{
                  ...styles.paginationButton,
                  ...(currentPage === 1 || totalPages === 0
                    ? styles.paginationButtonDisabled
                    : {}),
                }}
                aria-label="Previous page"
              >
                &laquo;
              </button>

              {/* Page numbers */}
              {getPageNumbers().map((pageNum, index) =>
                pageNum === "..." ? (
                  <span key={`ellipsis-${index}`} style={{ margin: "0 4px" }}>
                    ...
                  </span>
                ) : (
                  <button
                    key={pageNum}
                    onClick={() => jumpToPage(pageNum)}
                    style={{
                      ...styles.paginationButton,
                      ...(Number(currentPage) === Number(pageNum)
                        ? styles.paginationButtonActive
                        : {}), // Ensuring type consistency
                    }}
                  >
                    {pageNum}
                  </button>
                )
              )}

              {/* Next page */}
              <button
                onClick={() => jumpToPage(currentPage + 1)}
                disabled={currentPage >= totalPages || totalPages === 0}
                style={{
                  ...styles.paginationButton,
                  ...(currentPage >= totalPages || totalPages === 0
                    ? styles.paginationButtonDisabled
                    : {}),
                }}
                aria-label="Next page"
              >
                &raquo;
              </button>

              {/* Jump to last page */}
              <button
                onClick={() => jumpToPage(totalPages)}
                disabled={currentPage >= totalPages || totalPages === 0}
                style={{
                  ...styles.paginationButton,
                  ...(currentPage >= totalPages || totalPages === 0
                    ? styles.paginationButtonDisabled
                    : {}),
                }}
                aria-label="Last page"
              >
                &raquo;&raquo;
              </button>

              {/* Direct page input for larger datasets */}
              {totalPages > 7 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginLeft: "8px",
                  }}
                >
                  <span style={{ fontSize: "0.875rem", marginRight: "4px" }}>
                    Go to:
                  </span>
                  <input
                    type="number"
                    min="1"
                    max={totalPages}
                    value={currentPage}
                    onChange={handlePageInputChange}
                    onBlur={(e) => {
                      const value = parseInt(e.target.value);
                      if (!isNaN(value) && value >= 1 && value <= totalPages) {
                        jumpToPage(value);
                      } else {
                        e.target.value = currentPage;
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const value = parseInt(e.target.value);
                        if (
                          !isNaN(value) &&
                          value >= 1 &&
                          value <= totalPages
                        ) {
                          jumpToPage(value);
                        } else {
                          e.target.value = currentPage;
                        }
                      }
                    }}
                    style={{
                      width: "50px",
                      padding: "4px 8px",
                      textAlign: "center",
                      border: "1px solid #d1d5db",
                      borderRadius: "4px",
                      fontSize: "0.875rem",
                    }}
                  />
                  <span style={styles.pageInfo}>of {totalPages}</span>
                </div>
              )}
            </div>
          </div>

          {/* Items per page selector and Pagination Mode Switch */}
          <div style={styles.itemsPerPageContainer}>
            <div style={styles.paginationModeSwitch}>
              <label style={styles.switchInput}></label>
            </div>

            <div style={{ display: "flex", alignItems: "center" }}>
              <label htmlFor="itemsPerPage" style={styles.itemsPerPageLabel}>
                {t("Items per page:")}
              </label>
              <select
                id="itemsPerPage"
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                style={styles.itemsPerPageSelect}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserGames;
